// The wall: a read-only, fullscreen view of everything happening in the
// workspace, meant for a TV or a second monitor. Everything on it comes
// from one WebSocket (hooks/useWallSocket.ts → API routes/wall); nothing
// here can change anything. Always dark, scaled to the viewport width.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type {
  WallActivity,
  WallAgent,
  WallApproval,
  WallHourBucket,
  WallProject,
  WallRun,
  WallSnapshot,
} from '@goblic/shared'
import { useWallSocket, type WallSocketStatus } from '../hooks/useWallSocket'
import { formatMoney, humanize } from '../lib/format'
import { roleAvatarUrl, useIllustrations } from '../lib/illustrations'

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const WORKING = new Set(['provisioning', 'running', 'verifying'])
const WAITING = new Set(['awaiting_approval', 'paused'])

/** Re-renders every `ms` so clocks and elapsed times keep moving. */
function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(timer)
  }, [ms])
  return now
}

function elapsed(from: string | null, now: number): string {
  if (!from) return '—'
  const s = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
}

function ago(from: string, now: number): string {
  const s = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}d`
}

function shortNumber(n: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

function clockTime(value: string): string {
  return new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** Scale the whole page with the screen: 1rem ≈ 0.85vw, between 12 and 24px. */
function useWallScale(): void {
  useEffect(() => {
    const root = document.documentElement
    const previous = root.style.fontSize
    root.style.fontSize = 'clamp(12px, 0.85vw, 24px)'
    return () => {
      root.style.fontSize = previous
    }
  }, [])
}

/** Hide the cursor after a few seconds without movement (it's a wall). */
function useIdleCursor(): boolean {
  const [idle, setIdle] = useState(false)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const wake = (): void => {
      setIdle(false)
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => setIdle(true), 3500)
    }
    wake()
    window.addEventListener('mousemove', wake)
    window.addEventListener('keydown', wake)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('mousemove', wake)
      window.removeEventListener('keydown', wake)
    }
  }, [])
  return idle
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function WallPage() {
  useWallScale()
  const idle = useIdleCursor()
  const { status, snapshot, feed } = useWallSocket()
  const now = useNow(1000)
  const toasts = useFinishToasts(snapshot)

  useEffect(() => {
    const previous = document.title
    document.title = snapshot ? `${snapshot.workspace.name} · Wall · Goblic` : 'Wall · Goblic'
    return () => {
      document.title = previous
    }
  }, [snapshot?.workspace.name])

  return (
    <div className={`wall-root ${idle ? 'cursor-none' : ''}`}>
      <div className="wall-aurora" aria-hidden />
      {status === 'unauthorized' ? (
        <WallMessage title="Your session has ended" body={<Link to="/login" className="text-moss underline">Sign in again</Link>} />
      ) : !snapshot ? (
        <WallMessage title="Connecting to the workshop…" body="Waiting for the first picture of the workspace." />
      ) : (
        <WallBoard snapshot={snapshot} feed={feed} status={status} now={now} idle={idle} />
      )}
      <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2">
        {toasts.map((run) => (
          <FinishToast key={run.id} run={run} />
        ))}
      </div>
    </div>
  )
}

function WallMessage({ title, body }: { title: string; body: ReactNode }) {
  return (
    <div className="relative z-10 flex h-full flex-col items-center justify-center gap-3 text-center">
      <span className="wall-pulse-dot h-3 w-3 rounded-full bg-moss" />
      <h1 className="font-display text-xl font-bold text-ink">{title}</h1>
      <p className="text-md text-ink-2">{body}</p>
    </div>
  )
}

function WallBoard({
  snapshot,
  feed,
  status,
  now,
  idle,
}: {
  snapshot: WallSnapshot
  feed: WallActivity[]
  status: WallSocketStatus
  now: number
  idle: boolean
}) {
  /** The newest working line per run, for the ticker on each run card. */
  const tickerByRun = useMemo(() => {
    const map = new Map<string, WallActivity>()
    for (const item of feed) {
      if (map.has(item.runId)) continue
      if (item.kind === 'status' || item.kind === 'approval') continue
      map.set(item.runId, item)
    }
    return map
  }, [feed])

  return (
    <div className="relative z-10 grid h-full grid-cols-12 grid-rows-[auto_auto_minmax(0,1fr)_minmax(0,34%)] gap-[1.1rem] p-[1.4rem]">
      <Header snapshot={snapshot} status={status} now={now} idle={idle} />
      <Kpis snapshot={snapshot} />
      <Panel className="col-span-8" title="On the floor" meta={`${snapshot.runs.length} live`}>
        <RunsFloor runs={snapshot.runs} tickers={tickerByRun} now={now} />
      </Panel>
      <div className="col-span-4 grid min-h-0 grid-rows-[minmax(0,1.25fr)_minmax(0,1fr)] gap-[1.1rem]">
        <Panel
          title="Needs a human"
          meta={snapshot.approvals.length ? `${snapshot.approvals.length} waiting` : 'all clear'}
          tone={snapshot.approvals.length ? 'signal' : undefined}
        >
          <Approvals approvals={snapshot.approvals} now={now} />
        </Panel>
        <Panel title="The crew" meta={`${snapshot.agents.filter((a) => a.state !== 'idle').length}/${snapshot.agents.length} busy`}>
          <Crew agents={snapshot.agents} />
        </Panel>
      </div>
      <Panel className="col-span-4" title="Projects" meta="queue → done today">
        <Projects projects={snapshot.projects} />
      </Panel>
      <Panel className="col-span-4" title="Last 24 hours" meta={`${snapshot.kpis.finishedToday} runs finished today`}>
        <Throughput buckets={snapshot.throughput} recent={snapshot.recentRuns} now={now} />
      </Panel>
      <Panel className="col-span-4" title="Live feed" meta={<LiveDot status={status} />}>
        <Feed feed={feed} />
      </Panel>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header + KPIs
// ---------------------------------------------------------------------------

function Header({ snapshot, status, now, idle }: { snapshot: WallSnapshot; status: WallSocketStatus; now: number; idle: boolean }) {
  const date = new Date(now)
  const [fullscreen, setFullscreen] = useState(() => !!document.fullscreenElement)
  useEffect(() => {
    const onChange = (): void => setFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])
  const toggle = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void document.documentElement.requestFullscreen().catch(() => undefined)
  }

  return (
    <header className="col-span-12 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <img src="/brand/logo-128.webp" alt="" className="h-[2.6rem] w-[2.6rem]" />
        <div>
          <div className="font-display text-[1.7rem] font-bold leading-none tracking-tight text-ink">{snapshot.workspace.name}</div>
          <div className="mt-1 flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-ink-3">
            <span>Goblic workshop</span>
            <span aria-hidden>·</span>
            <LiveDot status={status} />
          </div>
        </div>
      </div>
      <div className={`flex items-center gap-3 transition-opacity duration-500 ${idle ? 'opacity-0' : 'opacity-100'}`}>
        <Link to="/" className="wall-chip hover:text-ink">Back to Goblic</Link>
        <button type="button" onClick={toggle} className="wall-chip hover:text-ink">
          {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        </button>
      </div>
      <div className="text-right">
        <div className="font-mono text-[2.4rem] font-medium leading-none tabular-nums text-ink">
          {new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            .formatToParts(date)
            .map((part, i) => (
              <span key={i} className={part.type === 'second' || part.type === 'dayPeriod' ? 'text-ink-3' : undefined}>
                {part.type === 'dayPeriod' ? part.value.toLowerCase() : part.value}
              </span>
            ))}
        </div>
        <div className="mt-1 text-sm uppercase tracking-[0.18em] text-ink-3">
          {date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
      </div>
    </header>
  )
}

function LiveDot({ status }: { status: WallSocketStatus }) {
  const live = status === 'open'
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.18em] ${live ? 'text-moss' : 'text-signal'}`}>
      <span className={`h-2 w-2 rounded-full ${live ? 'wall-pulse-dot bg-moss' : 'bg-signal'}`} />
      {live ? 'Live' : 'Reconnecting'}
    </span>
  )
}

function Kpis({ snapshot }: { snapshot: WallSnapshot }) {
  const k = snapshot.kpis
  const finished = k.succeededToday + k.failedToday
  const rate = finished > 0 ? k.succeededToday / finished : null
  return (
    <div className="col-span-12 grid grid-cols-6 gap-[1.1rem]">
      <Kpi label="Working now" value={k.runningRuns} sub={`${k.queuedRuns} queued`} tone={k.runningRuns > 0 ? 'moss' : undefined} pulse={k.runningRuns > 0} />
      <Kpi
        label="Waiting on people"
        value={k.pendingApprovals}
        sub={`${k.waitingRuns} ${k.waitingRuns === 1 ? 'run' : 'runs'} on hold`}
        tone={k.pendingApprovals > 0 ? 'signal' : undefined}
      />
      <Kpi label="Runs finished today" value={finished} sub={`${k.succeededToday} ok · ${k.failedToday} failed`} aside={<RateRing rate={rate} />} />
      <Kpi label="Tasks done today" value={k.tasksDoneToday} sub={`${k.openTasks} still open`} />
      <Kpi label="Spend today" value={formatMoney(k.costTodayCents)} sub={`${shortNumber(k.tokensToday)} tokens`} />
      <Kpi label="Agents on duty" value={k.activeAgents} sub={`${snapshot.agents.filter((a) => a.state === 'working').length} working`} />
    </div>
  )
}

function Kpi({
  label,
  value,
  sub,
  tone,
  pulse,
  aside,
}: {
  label: string
  value: number | string
  sub: string
  tone?: 'moss' | 'signal'
  pulse?: boolean
  aside?: ReactNode
}) {
  const accent = tone === 'moss' ? 'wall-kpi-moss' : tone === 'signal' ? 'wall-kpi-signal' : ''
  return (
    <div className={`wall-glass wall-kpi ${accent} flex items-center justify-between px-[1.2rem] py-[0.95rem]`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-ink-3">
          {pulse && <span className="wall-pulse-dot h-2 w-2 rounded-full bg-moss" />}
          {label}
        </div>
        <div key={String(value)} className="wall-pop mt-1 font-display text-[2.6rem] font-bold leading-none tabular-nums text-ink">
          {value}
        </div>
        <div className="mt-1.5 truncate text-sm text-ink-2">{sub}</div>
      </div>
      {aside}
    </div>
  )
}

/** Today's success rate as a ring; empty ring when nothing finished yet. */
function RateRing({ rate }: { rate: number | null }) {
  const r = 26
  const c = 2 * Math.PI * r
  const tone = rate == null ? 'text-ink-3' : rate >= 0.8 ? 'text-ok' : rate >= 0.5 ? 'text-signal' : 'text-danger'
  return (
    <div className="relative h-[4.2rem] w-[4.2rem] shrink-0" title="Share of today's finished runs that succeeded">
      <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="rgb(var(--line-strong))" strokeWidth="6" />
        {rate != null && (
          <circle
            cx="32"
            cy="32"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${c * rate} ${c}`}
            className={`${tone} transition-[stroke-dasharray] duration-1000`}
          />
        )}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums text-ink">
        {rate == null ? '—' : `${Math.round(rate * 100)}%`}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function Panel({
  title,
  meta,
  tone,
  className = '',
  children,
}: {
  title: string
  meta?: ReactNode
  tone?: 'signal'
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`wall-glass flex min-h-0 flex-col overflow-hidden ${tone === 'signal' ? 'wall-glass-signal' : ''} ${className}`}>
      <div className="flex items-baseline justify-between px-[1.2rem] pb-2 pt-[0.9rem]">
        <h2 className="font-display text-md font-bold uppercase tracking-[0.16em] text-ink-2">{title}</h2>
        <div className="text-sm text-ink-3">{meta}</div>
      </div>
      <div className="relative min-h-0 flex-1 px-[1.2rem] pb-[1rem]">{children}</div>
    </section>
  )
}

function Avatar({ roleName, name, size, ring }: { roleName: string | null; name: string; size: string; ring?: 'working' | 'waiting' | 'idle' }) {
  const [illustrations] = useIllustrations()
  const ringClass = ring === 'working' ? 'wall-ring-working' : ring === 'waiting' ? 'wall-ring-waiting' : 'wall-ring-idle'
  return (
    <span className={`wall-avatar ${ringClass}`} style={{ width: size, height: size }}>
      {illustrations ? (
        <img src={roleAvatarUrl(roleName)} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-full bg-sunken font-display font-bold text-ink-2">
          {name.slice(0, 1).toUpperCase()}
        </span>
      )}
    </span>
  )
}

function StatusPill({ status }: { status: string }) {
  const cls = WORKING.has(status)
    ? 'bg-moss-soft text-moss-strong'
    : WAITING.has(status)
      ? 'bg-signal-soft text-signal'
      : status === 'completed'
        ? 'bg-ok-soft text-ok'
        : status === 'queued'
          ? 'bg-sunken text-ink-2'
          : 'bg-danger-soft text-danger'
  const glyph = WORKING.has(status) ? '●' : WAITING.has(status) ? '❚❚' : status === 'completed' ? '✓' : status === 'queued' ? '…' : '✕'
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${cls}`}>
      <span aria-hidden className="text-xs">{glyph}</span>
      {humanize(status)}
    </span>
  )
}

function Ticket({ ticket }: { ticket: string | null }) {
  if (!ticket) return null
  return <span className="shrink-0 whitespace-nowrap rounded bg-sunken px-1.5 py-0.5 font-mono text-sm font-medium text-ink-2">{ticket}</span>
}

// --- Runs ------------------------------------------------------------------

function RunsFloor({ runs, tickers, now }: { runs: WallRun[]; tickers: Map<string, WallActivity>; now: number }) {
  if (runs.length === 0) {
    return (
      <div className="flex h-full items-center justify-center gap-8">
        <img src="/goblins/scene-sleeping.webp" alt="" className="max-h-[60%] w-auto max-w-[45%] object-contain" />
        <div>
          <div className="font-display text-xl font-bold text-ink">All quiet on the floor</div>
          <div className="mt-1 text-md text-ink-2">No agent is running right now. New runs show up here the moment they start.</div>
        </div>
      </div>
    )
  }
  return <RunsGrid runs={runs} tickers={tickers} now={now} />
}

const CARD_MIN_REM = 20
const GAP_REM = 0.9

type CardDensity = 'full' | 'medium' | 'compact'

/**
 * Lays the floor out so every card fits: as few columns as the runs need,
 * rows that share the height, and per-card detail that drops as rows get
 * shorter (full → medium → compact); past that, a "+N more" tile.
 */
function RunsGrid({ runs, tickers, now }: { runs: WallRun[]; tickers: Map<string, WallActivity>; now: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 1200, height: 600, rem: 16 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = (): void =>
      setBox({ width: el.clientWidth, height: el.clientHeight, rem: parseFloat(getComputedStyle(document.documentElement).fontSize) || 16 })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Working first, then waiting, then queued; most recent start first within each.
  const order = (run: WallRun): number => (WORKING.has(run.status) ? 0 : WAITING.has(run.status) ? 1 : 2)
  const sorted = [...runs].sort((a, b) => order(a) - order(b) || (b.startedAt ?? b.createdAt).localeCompare(a.startedAt ?? a.createdAt))

  const rem = box.rem
  const gap = GAP_REM * rem
  const maxCols = Math.max(1, Math.floor((box.width + gap) / (CARD_MIN_REM * rem + gap)))
  const minRow = 4.6 * rem
  const maxRows = Math.max(1, Math.floor((box.height + gap) / (minRow + gap)))
  const capacity = maxCols * maxRows
  const overflow = sorted.length > capacity ? sorted.length - capacity + 1 : 0
  const shown = overflow ? sorted.slice(0, capacity - 1) : sorted
  const tiles = shown.length + (overflow ? 1 : 0)
  // Fewest rows first: fill the width before adding rows.
  const rows = Math.max(1, Math.ceil(tiles / maxCols))
  const cols = Math.max(1, Math.min(maxCols, Math.ceil(tiles / rows)))
  const rowHeight = Math.min(15 * rem, (box.height - gap * (rows - 1)) / rows)
  const density: CardDensity = rowHeight >= 13 * rem ? 'full' : rowHeight >= 8.6 * rem ? 'medium' : 'compact'

  return (
    <div
      ref={ref}
      className="grid h-full overflow-hidden"
      style={{
        gap,
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, ${Math.floor(rowHeight)}px)`,
        alignContent: 'start',
      }}
    >
      {shown.map((run) => (
        <RunCard key={run.id} run={run} ticker={tickers.get(run.id) ?? null} now={now} density={density} />
      ))}
      {overflow > 0 && (
        <div className="wall-card flex items-center justify-center font-display text-lg font-bold text-ink-2">
          +{overflow} more live
        </div>
      )}
    </div>
  )
}

function RunCard({ run, ticker, now, density }: { run: WallRun; ticker: WallActivity | null; now: number; density: CardDensity }) {
  const working = WORKING.has(run.status)
  const waiting = WAITING.has(run.status)
  const tickerLine = ticker ? (
    <div key={ticker.id} className="wall-ticker-line">
      <span className="mr-2 text-ink-3">{KIND_GLYPH[ticker.kind]}</span>
      <span className={ticker.kind === 'message' ? 'italic text-ink-2' : 'text-ink-2'}>{ticker.text}</span>
    </div>
  ) : (
    <div className="wall-ticker-line text-ink-3">{run.status === 'queued' ? 'Waiting for a sandbox…' : 'Warming up…'}</div>
  )
  if (density === 'medium') {
    return (
      <article className={`wall-card wall-enter flex flex-col justify-center ${working ? 'wall-card-working' : waiting ? 'wall-card-waiting' : ''}`}>
        <div className="flex items-center gap-3">
          <Avatar roleName={run.roleName} name={run.agentName} size="3rem" ring={working ? 'working' : waiting ? 'waiting' : 'idle'} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-display text-md font-bold text-ink">{run.agentName}</span>
              <span className="ml-auto font-mono text-md tabular-nums text-ink">{elapsed(run.startedAt ?? run.createdAt, now)}</span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Ticket ticket={run.ticket} />
              <StatusPill status={run.status} />
              <span className="truncate text-sm text-ink-3">{run.roleName}</span>
            </div>
          </div>
        </div>
        <div className="mt-2 truncate text-base font-medium text-ink">{run.taskTitle}</div>
        <div className="wall-ticker mt-2">{tickerLine}</div>
        {working && <div className="wall-scan" aria-hidden />}
      </article>
    )
  }
  if (density === 'compact') {
    return (
      <article className={`wall-card wall-enter flex items-center gap-3 !py-2 ${working ? 'wall-card-working' : waiting ? 'wall-card-waiting' : ''}`}>
        <Avatar roleName={run.roleName} name={run.agentName} size="2.8rem" ring={working ? 'working' : waiting ? 'waiting' : 'idle'} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-base font-bold text-ink">{run.agentName}</span>
            <Ticket ticket={run.ticket} />
            <StatusPill status={run.status} />
            <span className="ml-auto font-mono text-base tabular-nums text-ink">{elapsed(run.startedAt ?? run.createdAt, now)}</span>
          </div>
          <div className="mt-0.5 truncate text-sm text-ink-2">{ticker && ticker.kind !== 'status' ? ticker.text : run.taskTitle}</div>
        </div>
        {working && <div className="wall-scan" aria-hidden />}
      </article>
    )
  }
  return (
    <article className={`wall-card wall-enter ${working ? 'wall-card-working' : waiting ? 'wall-card-waiting' : ''}`}>
      <div className="flex items-start gap-3">
        <Avatar roleName={run.roleName} name={run.agentName} size="3.4rem" ring={working ? 'working' : waiting ? 'waiting' : 'idle'} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-display text-md font-bold text-ink">{run.agentName}</div>
            <div className="font-mono text-md tabular-nums text-ink">{elapsed(run.startedAt ?? run.createdAt, now)}</div>
          </div>
          <div className="truncate text-sm text-ink-3">
            {[run.roleName, run.model, run.kind === 'decision' ? 'decision' : null].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-2">
        <Ticket ticket={run.ticket} />
        <StatusPill status={run.status} />
        {run.workflowState && <span className="truncate text-sm text-ink-3">{humanize(run.workflowState)}</span>}
      </div>
      <div className="mt-2 line-clamp-2 text-base font-medium leading-snug text-ink">{run.taskTitle}</div>
      <div className="wall-ticker mt-2.5">
        {tickerLine}
      </div>
      <div className="mt-2.5 flex items-center gap-4 text-sm tabular-nums text-ink-3">
        <span>{run.totalTokens != null ? `${shortNumber(run.totalTokens)} tokens` : '— tokens'}</span>
        <span>{run.toolCallsUsed} tools</span>
        <span>{formatMoney(run.costCents)}</span>
      </div>
      {working && <div className="wall-scan" aria-hidden />}
    </article>
  )
}

// --- Approvals -------------------------------------------------------------

const RISK_CLASS: Record<string, string> = {
  low: 'bg-sunken text-ink-2',
  medium: 'bg-info-soft text-info',
  high: 'bg-signal-soft text-signal',
  critical: 'bg-danger-soft text-danger',
}

function Approvals({ approvals, now }: { approvals: WallApproval[]; now: number }) {
  if (approvals.length === 0) {
    return (
      <div className="flex h-full items-center gap-5">
        <img src="/goblins/success.webp" alt="" className="h-[80%] max-h-[9rem] w-auto" />
        <div>
          <div className="font-display text-lg font-bold text-ink">Nothing waiting on you</div>
          <div className="text-base text-ink-2">The agents have everything they need.</div>
        </div>
      </div>
    )
  }
  return <ApprovalList approvals={approvals} now={now} />
}

/** Shows only the approvals that fit whole, and says how many more are waiting. */
function ApprovalList({ approvals, now }: { approvals: WallApproval[]; now: number }) {
  const listRef = useRef<HTMLUListElement>(null)
  const [visible, setVisible] = useState(approvals.length)

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return
    const fit = (): void => {
      const items = [...list.children] as HTMLElement[]
      const bottom = list.getBoundingClientRect().bottom
      // The "+N more" line sits below the list, so the list shrinks when it
      // appears and the observer measures again.
      let count = 0
      for (const item of items) {
        if (item.getBoundingClientRect().bottom <= bottom + 0.5) count++
        else break
      }
      setVisible(Math.max(1, count))
    }
    // Measure with every item rendered, then trim.
    setVisible(approvals.length)
    const frame = requestAnimationFrame(fit)
    const observer = new ResizeObserver(fit)
    observer.observe(list)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [approvals])

  const hidden = approvals.length - visible
  return (
    <div className="flex h-full flex-col">
      <ul ref={listRef} className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        {approvals.map((a, i) => (
          <li key={a.id} className="wall-enter wall-approval" style={i >= visible ? { visibility: 'hidden' } : undefined}>
            <div className="flex items-center gap-2">
              <Ticket ticket={a.ticket} />
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${RISK_CLASS[a.riskLevel] ?? RISK_CLASS.low}`}>
                {a.riskLevel}
              </span>
              <span className="truncate text-sm text-ink-3">{humanize(a.actionType)}</span>
              <span className="ml-auto shrink-0 font-mono text-sm tabular-nums text-signal">{ago(a.createdAt, now)}</span>
            </div>
            <div className={`mt-1 text-base text-ink ${approvals.length > 2 ? 'truncate' : 'line-clamp-2'}`}>{a.reason}</div>
            {(a.agentName || a.taskTitle) && (
              <div className="mt-0.5 truncate text-sm text-ink-3">{[a.agentName, a.taskTitle].filter(Boolean).join(' · ')}</div>
            )}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <div className="pt-1 text-sm font-semibold text-signal">
          +{hidden} more waiting
        </div>
      )}
    </div>
  )
}

// --- Crew --------------------------------------------------------------------

function Crew({ agents }: { agents: WallAgent[] }) {
  if (agents.length === 0) return <div className="text-base text-ink-3">No active agents in this workspace yet.</div>
  const order = { working: 0, waiting: 1, idle: 2 } as const
  const sorted = [...agents].sort((a, b) => order[a.state] - order[b.state] || a.name.localeCompare(b.name))
  const dense = agents.length > 6
  return (
    <div
      className={`wall-fade grid h-full auto-rows-min gap-x-3 overflow-hidden ${
        dense ? 'grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-y-2' : 'grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-y-3'
      }`}
    >
      {sorted.map((agent) => (
        <div key={agent.id} className={`flex items-center gap-2.5 ${agent.state === 'idle' ? 'opacity-55' : ''}`}>
          <Avatar roleName={agent.roleName} name={agent.name} size={dense ? '2.3rem' : '2.8rem'} ring={agent.state} />
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-ink">{agent.name}</div>
            <div className="truncate text-sm text-ink-3">
              {agent.state === 'idle' ? `${agent.runsToday} today` : agent.ticket ?? humanize(agent.state)}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// --- Projects ----------------------------------------------------------------

const STAGES: { key: keyof Pick<WallProject, 'inbox' | 'backlog' | 'ready' | 'active' | 'waiting' | 'doneToday'>; label: string; cls: string }[] = [
  { key: 'inbox', label: 'Inbox', cls: 'bg-ink-3/45' },
  { key: 'backlog', label: 'Backlog', cls: 'bg-ink-3/70' },
  { key: 'ready', label: 'Ready', cls: 'bg-ink-2' },
  { key: 'active', label: 'Active', cls: 'bg-info' },
  { key: 'waiting', label: 'Waiting', cls: 'bg-signal' },
  { key: 'doneToday', label: 'Done today', cls: 'bg-ok' },
]

function Projects({ projects }: { projects: WallProject[] }) {
  if (projects.length === 0) return <div className="text-base text-ink-3">No projects yet.</div>
  const max = Math.max(1, ...projects.map((p) => STAGES.reduce((sum, s) => sum + p[s.key], 0)))
  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-2">
        {STAGES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-sm ${s.cls}`} />
            {s.label}
          </span>
        ))}
      </div>
      <ul className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        {projects.map((p) => {
          const total = STAGES.reduce((sum, s) => sum + p[s.key], 0)
          return (
            <li key={p.id}>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-base font-semibold text-ink">
                  <span className="mr-2 font-mono text-sm text-ink-3">{p.key}</span>
                  {p.name}
                </span>
                <span className="shrink-0 text-sm tabular-nums text-ink-2">
                  {p.active > 0 && <span className="mr-3">{p.active} active</span>}
                  {p.doneToday > 0 && <span>{p.doneToday} done</span>}
                </span>
              </div>
              <div className="flex h-3 w-full gap-[2px]" style={{ width: `${Math.max(6, (total / max) * 100)}%` }}>
                {total === 0 ? (
                  <div className="h-full w-full rounded-[3px] bg-line" title="Nothing queued" />
                ) : (
                  STAGES.filter((s) => p[s.key] > 0).map((s) => (
                    <div
                      key={s.key}
                      title={`${s.label}: ${p[s.key]}`}
                      className={`h-full rounded-[3px] transition-[flex-grow] duration-700 ${s.cls}`}
                      style={{ flexGrow: p[s.key], flexBasis: 0 }}
                    />
                  ))
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// --- Throughput ----------------------------------------------------------------

function Throughput({ buckets, recent, now }: { buckets: WallHourBucket[]; recent: WallRun[]; now: number }) {
  const [hover, setHover] = useState<number | null>(null)
  const max = Math.max(1, ...buckets.map((b) => b.succeeded + b.failed))
  const W = 480
  const H = 120
  const gap = 4
  const bw = (W - gap * (buckets.length - 1)) / buckets.length
  const y = (v: number): number => (v / max) * (H - 4)
  const hovered = hover != null ? buckets[hover] : null
  return (
    <div className="flex h-full flex-col">
      <div className="mb-1 flex items-center gap-4 text-sm text-ink-2">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-ok" />Succeeded</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-danger" />Failed</span>
        <span className="ml-auto tabular-nums text-ink-3">
          {hovered
            ? `${new Date(hovered.hour).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} · ${hovered.succeeded} ok · ${hovered.failed} failed`
            : `peak ${max}/h`}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H + 16}`} className="h-[46%] w-full" preserveAspectRatio="none" onMouseLeave={() => setHover(null)}>
        <line x1="0" x2={W} y1={H} y2={H} stroke="rgb(var(--line-strong))" strokeWidth="1" />
        {buckets.map((b, i) => {
          const x = i * (bw + gap)
          const ok = y(b.succeeded)
          const bad = y(b.failed)
          const dim = hover != null && hover !== i
          return (
            <g key={b.hour} opacity={dim ? 0.45 : 1} onMouseEnter={() => setHover(i)}>
              <rect x={x} y={0} width={bw + gap} height={H} fill="transparent" />
              {b.succeeded > 0 && <rect x={x} y={H - ok} width={bw} height={ok} rx={3} className="fill-ok" />}
              {b.failed > 0 && <rect x={x} y={H - ok - bad - (ok > 0 ? 2 : 0)} width={bw} height={bad} rx={3} className="fill-danger" />}
              {b.succeeded + b.failed === 0 && <rect x={x} y={H - 2} width={bw} height={2} rx={1} fill="rgb(var(--line-strong))" />}
              {i % 6 === 0 && (
                <text x={x} y={H + 13} className="fill-ink-3" fontSize="10">
                  {new Date(b.hour).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-ink-3">Just finished</div>
      <ul className="mt-1.5 flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden">
        {recent.map((run) => (
          <li key={run.id} className="flex items-center gap-2 text-base">
            <span className={`w-4 text-center ${run.status === 'completed' ? 'text-ok' : run.status === 'cancelled' || run.status === 'handed_off' ? 'text-ink-3' : 'text-danger'}`} aria-hidden>
              {run.status === 'completed' ? '✓' : run.status === 'cancelled' || run.status === 'handed_off' ? '–' : '✕'}
            </span>
            <Ticket ticket={run.ticket} />
            <span className="truncate text-ink">{run.taskTitle}</span>
            <span className="ml-auto shrink-0 text-sm text-ink-3">
              {run.agentName} · {run.finishedAt ? ago(run.finishedAt, now) : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// --- Feed --------------------------------------------------------------------

const KIND_GLYPH: Record<WallActivity['kind'], string> = {
  status: '◆',
  message: '›',
  tool: '⚒',
  file: '✎',
  tests: '⚑',
  approval: '✋',
  error: '!',
  knowledge: '❖',
  report: '▤',
  checkpoint: '⟳',
}

const TONE_DOT: Record<WallActivity['tone'], string> = {
  neutral: 'bg-ink-3',
  ok: 'bg-ok',
  info: 'bg-info',
  signal: 'bg-signal',
  danger: 'bg-danger',
}

function Feed({ feed }: { feed: WallActivity[] }) {
  if (feed.length === 0) return <div className="text-base text-ink-3">Nothing has happened in the last day.</div>
  return (
    <ul className="wall-feed flex h-full flex-col gap-1 overflow-hidden">
      {feed.slice(0, 40).map((item) => (
        <li key={item.id} className="wall-feed-item flex items-baseline gap-2 text-sm">
          <span className="shrink-0 font-mono tabular-nums text-ink-3">{clockTime(item.at)}</span>
          <span className={`relative top-[-1px] h-2 w-2 shrink-0 self-center rounded-full ${TONE_DOT[item.tone]}`} />
          <span className="shrink-0 font-semibold text-ink">{item.agentName ?? 'agent'}</span>
          {item.ticket && <span className="shrink-0 font-mono text-ink-3">{item.ticket}</span>}
          <span className={`truncate ${item.kind === 'message' ? 'italic text-ink-3' : 'text-ink-2'}`}>
            <span className="mr-1 not-italic text-ink-3">{KIND_GLYPH[item.kind]}</span>
            {item.text}
          </span>
        </li>
      ))}
    </ul>
  )
}

// --- Finish toasts ---------------------------------------------------------------

/** Runs that finished since the wall opened, each shown for a few seconds. */
function useFinishToasts(snapshot: WallSnapshot | null): WallRun[] {
  const seen = useRef<Set<string> | null>(null)
  const [toasts, setToasts] = useState<WallRun[]>([])
  useEffect(() => {
    if (!snapshot) return
    if (seen.current == null) {
      seen.current = new Set(snapshot.recentRuns.map((r) => r.id))
      return
    }
    const fresh = snapshot.recentRuns.filter((r) => !seen.current!.has(r.id))
    for (const run of fresh) seen.current.add(run.id)
    if (fresh.length === 0) return
    setToasts((current) => [...current, ...fresh].slice(-3))
    const ids = new Set(fresh.map((r) => r.id))
    // Not cleared on the next snapshot: each toast lives its full 7 seconds.
    setTimeout(() => setToasts((current) => current.filter((r) => !ids.has(r.id))), 7000)
  }, [snapshot])
  return toasts
}

function FinishToast({ run }: { run: WallRun }) {
  const ok = run.status === 'completed'
  return (
    <div className={`wall-toast ${ok ? 'wall-toast-ok' : 'wall-toast-bad'}`}>
      <img src={ok ? '/goblins/success.webp' : '/goblins/failed.webp'} alt="" className="h-[3.4rem] w-[3.4rem] object-contain" />
      <div className="min-w-0">
        <div className={`text-sm font-semibold uppercase tracking-[0.14em] ${ok ? 'text-ok' : 'text-danger'}`}>
          {ok ? 'Done' : humanize(run.status)} · {run.agentName}
        </div>
        <div className="max-w-[34rem] truncate font-display text-md font-bold text-ink">
          {run.ticket && <span className="mr-2 font-mono text-ink-2">{run.ticket}</span>}
          {run.taskTitle}
        </div>
      </div>
    </div>
  )
}
